import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { useAuth } from '@/auth/useAuth';
import { ROLE_LABELS, type RoleCode } from '@/auth/permissions';
import { NAV_ITEMS, visibleNavItems } from '@/shell/navigation';
import { useOpenAlertCount } from '@/api/queries/alerts';
import { useCommissioningProgress } from '@/api/queries/commissioning';
import { RoleSwitcher } from '@/auth/RoleSwitcher';
import { BrandLockup } from '@/shell/BrandLockup';
import { NavIcon } from '@/shell/NavIcon';
import { SkeletonLines } from '@/components/Skeleton';
import { UserMenu } from '@/shell/UserMenu';

/**
 * The app shell — W-0.8, in the UDC header of CHANGE-5.
 *
 * **Header** (white, UDC's own shadow), laid out as udcqatar.com lays out
 * its own: the UDC logo and the Parking Management lockup on the left, and on the
 * right two rows — the live alert count and the account menu above, the
 * uppercase navigation below. Two rows because nine items, a logo and an
 * account chip do not fit one row at the 1366 px floor (OPEN-W2), and a nav
 * that wraps or scrolls is worse than one that sits a row lower.
 *
 * **Status bar** (UDC's black footer with its red rule): the commissioning
 * count and the current route path — the path is what a screenshot needs to
 * be reproducible — and the facility time zone.
 *
 * The nav lists only what the role may open. Signed out it still lists the
 * nine, as inert, dimmed text: it tells a new arrival what the product
 * contains without offering a link that would only answer 403. Sign-in itself
 * happens outside this shell — see `AuthLayout` and CHANGE-1.
 *
 * Below `xl` (1280 px) the nav is a drawer over the page, opened from a
 * button in the header — the same element, so the permission filtering and
 * the accessible name are one implementation.
 *
 * The rail and its collapse control are gone (CHANGE-5): with the nav in the
 * header the plan has the full width it used to win only by collapsing.
 */
export function AppShell() {
  const config = getRuntimeConfig();
  const { me, has, status, booting } = useAuth();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const signedIn = status === 'signed-in' && me !== null;
  const items = signedIn ? visibleNavItems(has) : NAV_ITEMS;
  const resolving = booting;

  const { count: openAlerts } = useOpenAlertCount(signedIn && has('alerts.read'));
  const commissioning = useCommissioningProgress(signedIn && has('devices.read'));

  // Navigating is the end of the drawer's job; leaving it open over the page
  // the visitor just asked for is the classic mobile-nav bug.
  useEffect(() => setDrawerOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDrawerOpen(false);
      // Focus goes back where it came from, or it lands on the page body and
      // a keyboard user has to tab in from the top again.
      menuButtonRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const roleLabel = me?.roles.map((role) => ROLE_LABELS[role as RoleCode] ?? role).join(', ');
  const showAlertChip = !signedIn || has('alerts.read');

  /*
   * **Nothing of the application until the session is resolved.**
   *
   * `restore()` is an HTTP round-trip — against the real backend a signed-out
   * first load is a 401 that takes ~250 ms, and a cold App Service start is
   * far longer. For that whole window `status` is `loading`, and the shell
   * used to paint in full: the top bar, and a nav listing all nine routes,
   * because `signedIn` is false while loading exactly as it is false when
   * signed out. The route guards correctly render nothing, so what a visitor
   * saw was the complete application chrome around an empty page, and then a
   * redirect to `/login` once the request came back. That is the "dashboard
   * loads and then throws me to login" report.
   *
   * Loading is not signed-out. It is not-yet-known, and the honest thing to
   * draw for not-yet-known is the shell's own ground and nothing on it — so
   * the first frame a visitor sees is either the application or the sign-in
   * screen, never one dissolving into the other.
   */
  if (resolving) {
    return (
      <div
        className="flex h-screen flex-col items-center justify-center bg-pv-canvas"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">Signing you in…</span>
        <span
          aria-hidden="true"
          className="h-6 w-6 animate-spin rounded-full border-2 border-pv-line border-t-pv-primary motion-reduce:animate-none"
        />
      </div>
    );
  }

  return (
    /*
     * The shell is the window, not the document: the header and the status
     * bar stay put and only the content scrolls. On a screen holding a
     * 362-bay plan and a 362-row table, navigation that scrolls away is
     * navigation you have to scroll back up to reach.
     */
    <div className="flex h-screen flex-col overflow-hidden bg-pv-canvas">
      <header
        data-pv-chrome
        className="relative z-30 flex min-h-[76px] shrink-0 items-stretch gap-4 bg-pv-surface px-4 sm:px-5 xl:gap-7 xl:px-7"
        style={{ boxShadow: 'var(--pv-shadow-header)' }}
      >
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
          aria-controls="pv-main-nav"
          aria-label={drawerOpen ? 'Close the navigation' : 'Open the navigation'}
          className="-ml-1 shrink-0 self-center rounded p-1.5 text-pv-ink hover:text-pv-primary xl:hidden"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
          >
            {drawerOpen ? (
              <>
                <path d="m5 5 10 10" />
                <path d="m15 5-10 10" />
              </>
            ) : (
              <>
                <path d="M3 5.5h14" />
                <path d="M3 10h14" />
                <path d="M3 14.5h14" />
              </>
            )}
          </svg>
        </button>

        <BrandLockup
          className="min-w-0 self-center py-2.5"
          {...(signedIn ? { to: '/map' } : {})}
          facility={
            <>
              {config.facilityName}
              {/*
               * The subtitle is the first thing to go. It is context, not
               * identity, and at 390 px it was wrapping and shoving the alert
               * chip and the account menu off the right edge.
               */}
              <span className="hidden sm:inline"> · {config.facilitySubtitle}</span>
            </>
          }
        />

        <div className="ml-auto flex min-w-0 flex-col justify-center gap-1 py-2 xl:items-end">
          <div className="flex shrink-0 items-center justify-end gap-2 sm:gap-4">
            {showAlertChip ? (
              signedIn ? (
                <NavLink
                  to="/alerts?state=open"
                  className="flex items-center gap-2 rounded px-1.5 py-0.5 text-[0.75rem] font-bold text-pv-ink hover:text-pv-primary"
                >
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full"
                    style={{
                      background: 'var(--pv-accent)',
                      boxShadow: '0 0 0 3px color-mix(in srgb, var(--pv-accent) 25%, transparent)',
                    }}
                  />
                  <span className="pv-figure">
                    {openAlerts}
                    <span className="hidden sm:inline">
                      {' '}
                      {openAlerts === 1 ? 'alert' : 'alerts'}
                    </span>
                  </span>
                </NavLink>
              ) : (
                <span className="flex items-center gap-2 px-2 py-1 font-bold text-pv-muted">
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full"
                    style={{ background: 'var(--pv-accent)' }}
                  />
                  <span className="pv-figure">0 alerts</span>
                </span>
              )
            ) : null}

            {signedIn ? (
              <UserMenu displayName={me.displayName} roleLabel={roleLabel ?? null} />
            ) : (
              <span className="border border-pv-line px-3 py-1.5 text-pv-muted">not signed in</span>
            )}
          </div>

          {/*
           * The scrim. Only below `xl`, where the nav is over the page rather
           * than in the header — a tap anywhere off the drawer closes it,
           * which is the gesture people already expect and saves the reach to
           * the X.
           */}
          {drawerOpen ? (
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 z-30 cursor-default bg-black/50 xl:hidden"
            />
          ) : null}

          <nav
            id="pv-main-nav"
            aria-label="Main"
            /*
             * Two layouts in one element. Below `xl`: fixed, over the page,
             * translated out of sight until opened. At `xl` and up: the
             * header's second row.
             */
            className={`fixed inset-y-0 left-0 z-40 flex w-[280px] flex-col overflow-y-auto bg-pv-surface px-3 py-4 transition-transform duration-200 xl:static xl:z-auto xl:w-auto xl:translate-x-0 xl:flex-row xl:overflow-visible xl:bg-transparent xl:p-0 xl:transition-none ${
              drawerOpen ? 'translate-x-0 shadow-[var(--pv-shadow-drawer)]' : '-translate-x-full'
            }`}
          >
            {/* Only while open: a second, always-mounted "Parking Management" would be
                one more copy of the name for tests and screen readers to find. */}
            {drawerOpen ? (
              <BrandLockup className="mb-4 border-b border-pv-line px-2 pb-4 xl:hidden" />
            ) : null}
            <ul className="flex flex-col gap-0.5 xl:flex-row xl:gap-0">
              {items.map((item) => (
                <li key={item.to}>
                  {signedIn ? (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        /*
                         * Active: red text plus a 2 px red bar — the bar is the
                         * shape that survives a bad monitor. In the drawer it
                         * stands on the left edge, in the header under the
                         * word, as on udcqatar.com.
                         */
                        `pv-nav-link relative flex items-center gap-3 px-3 py-2.5 xl:gap-0 xl:px-[11px] xl:pb-2 xl:pt-1.5 ${
                          isActive
                            ? 'text-pv-primary shadow-[inset_3px_0_0_0_var(--pv-primary)] xl:shadow-[inset_0_-2px_0_0_var(--pv-primary)]'
                            : 'text-pv-ink hover:text-pv-primary'
                        }`
                      }
                    >
                      <span className="xl:hidden">
                        <NavIcon to={item.to} />
                      </span>
                      <span>{item.label}</span>
                    </NavLink>
                  ) : (
                    <span
                      aria-disabled="true"
                      className="pv-nav-link flex items-center gap-3 px-3 py-2.5 text-pv-muted/70 xl:gap-0 xl:px-[11px] xl:pb-2 xl:pt-1.5"
                    >
                      <span className="xl:hidden">
                        <NavIcon to={item.to} />
                      </span>
                      <span>{item.label}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {/*
       * Full width. §4 sets a 1080 px content maximum, which is a reading
       * width: right for prose, wrong for a 362-bay plan, a fifteen-row area
       * table and a ten-column device list. Logged as CHANGE-2.
       */}
      <main className="min-w-0 flex-1 overflow-y-auto px-4 py-4 lg:px-6 lg:py-5">
        {/* Each screen is a lazily loaded chunk (src/router.tsx); the skeleton shows only on its first open. */}
        <Suspense fallback={<SkeletonLines lines={8} />}>
          <Outlet />
        </Suspense>
      </main>

      <footer
        data-pv-chrome
        className="flex shrink-0 items-center gap-x-5 gap-y-1 overflow-hidden border-t-[3px] border-pv-primary bg-pv-footer px-4 py-1.5 text-dense text-white sm:px-5 xl:px-7"
        aria-label="Status"
      >
        {commissioning.data ? (
          <span className="pv-figure shrink-0">
            {commissioning.data.mapped} / {commissioning.data.total} commissioned
          </span>
        ) : null}
        <span className="pv-figure min-w-0 truncate font-mono text-pv-band-label">
          {location.pathname}
          {location.search}
        </span>
        <span className="ml-auto hidden shrink-0 text-pv-band-label md:inline">
          Facility time · {config.facilityTimeZone}
        </span>
        {/* Room for the demo role switcher, which floats over this corner. */}
        <span aria-hidden="true" className="w-24 shrink-0" />
      </footer>

      <RoleSwitcher />
    </div>
  );
}
